/**
 * Pulls a likely tenant code from the current browser hostname.
 *
 * Rules (matches the backend resolution priority):
 * - `kristy.eshope.com` → `kristy`
 * - `sso.kristysoftware.com` → `sso` (any tenant subdomain works —
 *    the leading label falls through as the tenant code whenever it
 *    isn't one of the generic SKIP_PREFIXES below)
 * - `app.kristy.eshope.com` → `kristy` (skips common app/admin/panel prefixes)
 * - `kontic.com` → `kontic` (no subdomain → fall back to the domain's SLD)
 * - `kontic.co.in` → `kontic` (same; first label is the tenant)
 * - `localhost` / `127.0.0.1` / single-label hosts → undefined
 * - 192.168.x.x or other dotted IPs → undefined
 *
 * The result is only a *suggestion* for prefilling the login form and
 * for the `X-Tenant-Code` header on anonymous storefront reads (see
 * `apps/web/src/main.tsx`); the authoritative tenant resolution still
 * happens server-side from the full payload (`tenantCode`, `domain`,
 * `subdomain`, `TenantDomainMap`).
 *
 * Bug ref: SSO(2) Issue 2 — QR-scan on a fresh mobile browser had to
 * resolve `sso.kristysoftware.com` → `sso` without any prior session
 * or localStorage state. This function's generic first-label rule
 * covers that (and any future tenant subdomain) without a per-tenant
 * hardcoded mapping.
 */
// Storefront / subdomain prefixes that are NOT tenant codes — they
// label the SPA itself (admin / portal) or the storefront brand
// ("eshope", "store", "shop", "site"). When a hostname starts with
// one of these, we skip past it and look at the next label, OR
// return undefined and let the caller fall back to the default
// tenant. Without this list, `eshope.kristysoftware.com` resolved
// to a non-existent `eshope` tenant and the storefront returned
// zero products even though the real configured tenant had data.
//
// IMPORTANT: don't add real tenant codes here. `sso`, `knottique`,
// and any other tenant subdomain must fall through as the tenant.
const SKIP_PREFIXES = new Set([
  'www', 'app', 'admin', 'panel', 'web', 'portal',
  'eshope', 'store', 'shop', 'site', 'storefront',
  'api', 'm', 'mobile',
]);

export function resolveTenantFromHostname(host?: string): string | undefined {
  const h = (host ?? (typeof window !== 'undefined' ? window.location.hostname : '')).trim().toLowerCase();
  if (!h) return undefined;
  // Strip a trailing port if present (defensive — hostname usually excludes it).
  const noPort = h.split(':')[0];
  // Skip plain IPv4 addresses.
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(noPort)) return undefined;

  const labels = noPort.split('.').filter(Boolean);
  /* Need at least 2 labels (something.tld). Single-label hosts like
     `localhost` get rejected so the caller can fall back to its
     default tenant. Previously this required 3 labels which broke
     bare-domain deploys like `kontic.com` — the storefront then
     sent no X-Tenant-Code and the API used the host header, which
     on a multi-tenant deploy lands on the wrong tenant. Bug ref:
     AI Agent 2026-06-11 — "if sub domain not found then domain
     is tenant code". */
  if (labels.length < 2) return undefined;

  const first = labels[0];
  if (SKIP_PREFIXES.has(first)) {
    /* Skip the prefix and try the next label.
       - app.kristy.eshope.com → 'kristy'
       - www.kontic.com → 'kontic' (next label is the SLD, fine)
       Bail when the next label is ALSO a skip prefix to avoid
       returning a generic label as the tenant. */
    const next = labels[1];
    if (next && !SKIP_PREFIXES.has(next)) return next;
    return undefined;
  }
  /* First label is the tenant — works for both subdomain deploys
     (kristy.eshope.com) and bare-domain deploys (kontic.com,
     kontic.co.in). */
  return first;
}
